import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { SITE } from "@/data/site";

const LOADER_ID = "limoanywhere-widget-loader";

/**
 * Official LimoAnywhere ORES widget embed.
 * The loader script scans for <a data-ores-widget> anchors and swaps them
 * for an auto-resizing iframe, so we (re)inject the loader on every mount.
 * type: "quickres" (compact form) | "website" (full booking engine)
 */
export default function LimoWidget({ type = "website", minHeight = 560, testId = "limo-widget" }) {
  const boxRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const box = boxRef.current;
    const observer = new MutationObserver(() => {
      const frame = box?.querySelector("iframe");
      if (frame) {
        frame.addEventListener("load", () => setLoaded(true), { once: true });
        observer.disconnect();
      }
    });
    if (box) observer.observe(box, { childList: true, subtree: true });

    document.getElementById(LOADER_ID)?.remove();
    const s = document.createElement("script");
    s.id = LOADER_ID;
    s.src = SITE.bookingScript;
    s.async = true;
    s.onload = () => {
      // The loader assigns window.onmessage assuming string payloads; guard non-string messages.
      const orig = window.onmessage;
      window.onmessage = (e) => {
        if (typeof e.data === "string" && orig) orig.call(window, e);
      };
    };
    document.body.appendChild(s);

    const t = setTimeout(() => setSlow(true), 9000);
    return () => {
      clearTimeout(t);
      observer.disconnect();
    };
  }, []);

  const redirect = `${window.location.origin}/reservation`;

  return (
    <div
      className="gold-frame relative overflow-hidden rounded-2xl bg-obsidian-800"
      style={{ minHeight }}
      data-testid={`${testId}-container`}
    >
      {!loaded && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-6 bg-obsidian-800 px-6 text-center"
          data-testid={`${testId}-loading`}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
          >
            <Logo size={56} />
          </motion.div>
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
            Opening the booking engine…
          </p>
          {slow && (
            <a
              href={SITE.booking}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-fallback-link`}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-obsidian-900"
            >
              Open booking in a new window
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      )}
      <div ref={boxRef} className="relative p-2 sm:p-4">
        <a
          href={SITE.booking}
          data-ores-widget={type}
          data-ores-alias={SITE.bookingAlias}
          data-redirect-url={redirect}
          data-testid={`${testId}-anchor`}
          className="sr-only"
        >
          Online Reservations
        </a>
      </div>
    </div>
  );
}
